/**
 * Onboarding — nine steps, and the learner speaks at step three.
 *
 * This is the implementation of strategy wedge W1. The structural rules, which the tests
 * assert (docs/02-user-flows.md §1):
 *
 *   - microphone permission is requested at the moment it is obviously needed (step 3),
 *     not at step 18 the way the competitor does
 *   - the learner is scored before anything is asked of them
 *   - denying the microphone does not block onboarding; it falls back to a tap-the-words
 *     placement and the app stays fully usable
 *   - there is **no paywall anywhere in this flow**
 */

import {
  type AnxietySignal,
  anxietyAckCopy,
  commitmentProjection,
  DAILY_GOAL_CHOICES,
  DIALECTS_BY_LANGUAGE,
  type Dialect,
  hasDialectChoice,
  type Language,
  type PlanBasisRow,
  type PriorAppFailure,
  type PromiseCard,
  planBasis,
  promiseFor,
  promisesFor,
  type ScoreStatus,
  type SessionMode,
  shouldShowAnxietyAck,
} from '@vibelang/core';
import { Banner, Button, MicButton, ScoreRing, TargetSentence, WithheldNotice } from '@vibelang/ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { noTracking, type Track } from '../app-context.js';

export const ONBOARDING_STEPS = [
  // Not one of the nine numbered steps: it is skippable, it asks nothing, and counting
  // it would make the progress dots lie about how much is left.
  'value',
  'language',
  'dialect',
  'speak',
  'placement',
  // Not one of the nine either: it asks nothing, it advances by itself, and counting
  // it would make the dots overstate what is left.
  'plan_building',
  'motivation',
  // Also not one of the nine: it asks nothing, it is shown only on a signal, and
  // counting it would make the progress dots lie about how much is left.
  'anxiety',
  'commitment',
  'tutor',
  'notifications',
  'plan',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * The steps that do not appear in "Step N of 9".
 *
 * One rule, in one place: a step that asks the learner for nothing is not counted,
 * because counting it would make the dots overstate what is left. The list used to be
 * two names inlined twice in a filter, which is how a third one came to be counted.
 */
const UNCOUNTED_STEPS: ReadonlySet<OnboardingStep> = new Set(['value', 'anxiety', 'plan_building']);

export interface FirstScore {
  readonly overall: number | null;
  readonly band: string | null;
  /** The single most actionable note, shown verbatim. */
  readonly note: string | null;
  readonly withheld: boolean;
  readonly withheldMessage: string | null;
}

export interface OnboardingDraft {
  language: Language | null;
  dialect: Dialect | null;
  name: string;
  firstScore: FirstScore | null;
  micDenied: boolean;
  /** What went wrong with the app they tried before, or null if they have not said. */
  priorAppFailure: PriorAppFailure | null;
  /** Length of their first recorded utterance. Feeds the A6b signal. */
  firstUtteranceMs: number | null;
  /** Recordings they redid before accepting one. */
  speakRetries: number;
  /** A6b is shown once, ever. */
  anxietyAckSeen: boolean;
  /**
   * Which mode the first lesson opens in. Set by A6b's second action, and the reason
   * that action is not a dead end: text mode is a full path, so choosing it changes
   * nothing else about onboarding.
   */
  preferredMode: SessionMode;
  knownWordIds: string[];
  goals: string[];
  dailyGoalMinutes: number;
  reminderLocalTime: string;
  personaId: string;
  strictness: number;
  correctionStyle: 'inline' | 'after_turn' | 'end_of_session';
  notificationsAllowed: boolean | null;
}

/**
 * Three cards, each arguing against something the learner already believes.
 *
 * Taken from Speak ($3.25M/mo), whose carousel reads "Adults can become fluent — even
 * faster than kids" and "The secret? Speaking early and often, not memorizing
 * flashcards" (docs/research/06-leader-teardowns.md §2). A claim that contradicts the
 * reader gets read; a feature list does not.
 *
 * The third card is ours, and no competitor in the twenty studied can run it: it is the
 * confidence-gating promise, and it is the first thing a learner sees.
 */
const VALUE_CARDS: readonly { readonly claim: string; readonly support: string }[] = [
  {
    claim: 'You already know enough words.',
    support: "You've just never had to say them out loud.",
  },
  {
    claim: 'Ten minutes of talking beats an hour of tapping.',
    support: 'Speaking is the thing that transfers. Everything here is built around it.',
  },
  {
    claim: "We'll tell you when we're not sure.",
    support: 'If the microphone or the room is against us, you get no score — not a wrong one.',
  },
];

function ValueCarousel({
  onDone,
  track,
}: {
  readonly onDone: () => void;
  readonly track: Track;
}): JSX.Element {
  const [index, setIndex] = useState(0);
  const card = VALUE_CARDS[index];

  // Auto-advances, because a learner who is reading will tap and one who is not should
  // not have to. Never auto-advances off the last card: leaving is their decision.
  useEffect(() => {
    if (index >= VALUE_CARDS.length - 1) return;
    const timer = setTimeout(() => setIndex((n) => n + 1), 3200);
    return () => clearTimeout(timer);
  }, [index]);

  // One view event per card actually seen, including the ones the auto-advance reaches:
  // "how far did they read before leaving" is the only question this screen answers.
  useEffect(() => {
    track('onboarding_carousel_view', { card: index + 1 });
  }, [index, track]);

  const next = () => (index + 1 < VALUE_CARDS.length ? setIndex(index + 1) : onDone());

  const skip = () => {
    track('onboarding_carousel_skip', { at_card: index + 1 });
    onDone();
  };

  return (
    <section
      className="vl-value-carousel"
      aria-label="What VibeLang is for"
      aria-roledescription="carousel"
    >
      <p className="vl-value-claim">{card?.claim}</p>
      <p className="vl-value-support">{card?.support}</p>

      <div className="vl-value-dots" aria-hidden="true">
        {VALUE_CARDS.map((entry, position) => (
          <span key={entry.claim} data-active={position === index ? 'true' : 'false'} />
        ))}
      </div>

      <button type="button" className="vl-button vl-button--primary" onClick={next}>
        {index + 1 < VALUE_CARDS.length ? 'Next' : 'Start speaking'}
      </button>
      {/* Always escapable. A carousel you cannot leave is an advertisement. */}
      <button type="button" className="vl-button vl-button--ghost" onClick={skip}>
        Skip
      </button>
    </section>
  );
}

/**
 * The onboarding keeps its own narrow score shape, so it has to say which `ScoreStatus`
 * that corresponds to. `withheld` matters here: it is the one case the A6b screen exists
 * to explain, so it must not be reported as a score.
 */
function firstScoreStatus(score: FirstScore | null): ScoreStatus {
  if (score === null) return 'pending';
  if (score.withheld) return 'withheld';
  return score.overall === null ? 'unavailable' : 'scored';
}

export function emptyDraft(): OnboardingDraft {
  return {
    language: null,
    dialect: null,
    name: '',
    firstScore: null,
    micDenied: false,
    priorAppFailure: null,
    firstUtteranceMs: null,
    speakRetries: 0,
    anxietyAckSeen: false,
    preferredMode: 'voice',
    knownWordIds: [],
    goals: [],
    dailyGoalMinutes: 10,
    reminderLocalTime: '19:30',
    personaId: 'mika',
    strictness: 0.5,
    correctionStyle: 'after_turn',
    notificationsAllowed: null,
  };
}

const LANGUAGES: readonly { id: Language; name: string; endonym: string }[] = [
  { id: 'es', name: 'Spanish', endonym: 'Español' },
  { id: 'fr', name: 'French', endonym: 'Français' },
  { id: 'pt', name: 'Portuguese', endonym: 'Português' },
  { id: 'de', name: 'German', endonym: 'Deutsch' },
  { id: 'it', name: 'Italian', endonym: 'Italiano' },
  { id: 'ja', name: 'Japanese', endonym: '日本語' },
  { id: 'ko', name: 'Korean', endonym: '한국어' },
  { id: 'zh', name: 'Mandarin', endonym: '中文' },
  { id: 'en', name: 'English', endonym: 'English' },
];

const DIALECT_COPY: Partial<Record<Dialect, { name: string; detail: string }>> = {
  'es-MX': { name: 'Mexico & Latin America', detail: 'ustedes, seseo' },
  'es-ES': { name: 'Spain', detail: 'vosotros, distinción' },
  'pt-BR': { name: 'Brazil', detail: 'open vowels, você' },
  'pt-PT': { name: 'Portugal', detail: 'reduced vowels, tu' },
  'fr-FR': { name: 'France', detail: 'standard metropolitan' },
  'fr-CA': { name: 'Canada', detail: 'Québécois vowels' },
  'zh-CN': { name: 'Mainland', detail: 'simplified characters' },
  'zh-TW': { name: 'Taiwan', detail: 'traditional characters' },
  'en-US': { name: 'United States', detail: 'rhotic, flapped t' },
  'en-GB': { name: 'United Kingdom', detail: 'non-rhotic' },
};

const PERSONAS: readonly { id: string; name: string; character: string }[] = [
  { id: 'mika', name: 'Mika', character: 'Warm and patient' },
  { id: 'dani', name: 'Dani', character: 'Neutral and efficient' },
  { id: 'sol', name: 'Sol', character: 'Upbeat and encouraging' },
  { id: 'rex', name: 'Rex', character: 'Blunt, corrects everything' },
  { id: 'vex', name: 'Vex', character: 'Sarcastic — you asked for it' },
  { id: 'nakamura', name: 'Prof. Nakamura', character: 'Formal, exam-focused' },
];

const GOALS: readonly { id: string; label: string }[] = [
  { id: 'travel', label: 'Travel' },
  { id: 'work', label: 'Work' },
  { id: 'family', label: 'Family & relationships' },
  { id: 'exam', label: 'An exam' },
  { id: 'moving', label: 'Moving abroad' },
  { id: 'curiosity', label: 'Just curious' },
];

/** The sentence the learner says at step 3, per language. */
const FIRST_SENTENCE: Record<Language, { text: string; respelling: string; ipa: string }> = {
  es: { text: 'Hola, me llamo', respelling: 'OH-lah, meh YAH-moh', ipa: '/ˈola me ˈʎamo/' },
  fr: {
    text: "Bonjour, je m'appelle",
    respelling: 'bon-ZHOOR, zhuh ma-PELL',
    ipa: '/bɔ̃ʒuʁ ʒə mapɛl/',
  },
  pt: {
    text: 'Olá, eu me chamo',
    respelling: 'oh-LAH, eh-oo mee SHAH-moo',
    ipa: '/oˈla ew mi ˈʃãmu/',
  },
  de: { text: 'Hallo, ich heiße', respelling: 'HAH-loh, ikh HY-suh', ipa: '/haˈloː ɪç ˈhaɪsə/' },
  it: { text: 'Ciao, mi chiamo', respelling: 'chow, mee KYAH-moh', ipa: '/tʃao mi ˈkjamo/' },
  ja: {
    text: 'こんにちは、私は',
    respelling: 'kon-nee-chee-wah, wah-tah-shee wah',
    ipa: '/konnitɕiwa ɰataɕi wa/',
  },
  ko: {
    text: '안녕하세요, 제 이름은',
    respelling: 'an-nyong-ha-se-yo, je ee-reum-eun',
    ipa: '/annjʌŋhasejo/',
  },
  zh: { text: '你好，我叫', respelling: 'nee-HOW, wor JYAO', ipa: '/ni˨˩˦ xɑʊ̯˨˩˦ wo˨˩˦ tɕjɑʊ̯˥˩/' },
  en: { text: 'Hello, my name is', respelling: 'heh-LOH, my naym iz', ipa: '/həˈloʊ maɪ neɪm ɪz/' },
};

export interface OnboardingProps {
  readonly draft: OnboardingDraft;
  readonly onChange: (draft: OnboardingDraft) => void;
  readonly step: OnboardingStep;
  readonly onStep: (step: OnboardingStep) => void;
  /** Records and scores the learner's first utterance. */
  readonly onRecordFirstUtterance: () => Promise<FirstScore>;
  /**
   * How much has actually been published for the chosen dialect.
   *
   * The plan-building pause used to read the learner's own answers back to them. The
   * recorded competitors fill it with concrete outputs — "Designed 180 practical
   * scenarios" — and the difference is whether the wait feels like work being done
   * (docs/research/10-competitor-teardown-2026-10-18.md §5). Optional, because a count
   * we could not fetch is left out rather than guessed at.
   */
  readonly onFetchCatalogue?:
    | ((dialect: Dialect) => Promise<{ lessons: number; scenarios: number; items: number } | null>)
    | undefined;
  readonly onRequestMic: () => Promise<boolean>;
  readonly onRequestNotifications: () => Promise<boolean>;
  /** Plays a sample sentence in a dialect. A no-op is a valid implementation offline. */
  readonly onPreviewDialect?: ((dialect: Dialect) => void) | undefined;
  readonly onPreviewPersona?: ((personaId: string) => void) | undefined;
  /** Plays one side of the first-score A/B comparison. */
  readonly onPlayback?: ((which: 'you' | 'coach' | 'coach_slow') => void) | undefined;
  readonly onFinish: () => void;
  /** Placement word grid, loaded from the API. */
  readonly placementWords: readonly { id: string; display: string }[];
  readonly estimatedLevel: string | null;
  /**
   * Records a product event. A prop rather than a context read so this screen stays
   * renderable on its own, and so the funnel it measures is visible in its signature.
   * Defaults to doing nothing: a test that does not care about analytics says nothing
   * about analytics.
   */
  readonly track?: Track | undefined;
}

export function Onboarding(props: OnboardingProps): JSX.Element {
  const { draft, onChange, step, onStep } = props;
  const track = props.track ?? noTracking;

  // The onboarding's own clock, for `onboarding_complete {duration_ms}` and for the
  // north-star-adjacent `time_to_first_utterance_ms`. A ref, because a re-render must
  // not restart it.
  const startedAt = useRef(Date.now());
  const firstUtteranceReported = useRef(false);

  // One `onboarding_step_view` per step the learner actually reaches. The step index is
  // taken from the path they are on, not from the canonical nine, so a learner who
  // skipped the dialect question is not recorded as having seen step 3 twice.
  const seenSteps = useRef(new Set<OnboardingStep>());
  // Shown only on a signal, and only once. Computed here rather than inside the screen
  // so a learner who shows no sign of it never has the step in their path at all — the
  // progress indicator counts this list.
  const anxiety = useMemo(
    () =>
      shouldShowAnxietyAck({
        alreadySeen: draft.anxietyAckSeen,
        firstUtteranceMs: draft.firstUtteranceMs,
        retriesBeforeSubmit: draft.speakRetries,
        motivations: draft.goals,
        firstUtteranceStatus: firstScoreStatus(draft.firstScore),
        firstUtteranceScore: draft.firstScore?.overall ?? null,
      }),
    [
      draft.anxietyAckSeen,
      draft.firstUtteranceMs,
      draft.speakRetries,
      draft.goals,
      draft.firstScore,
    ],
  );

  // Single-variant languages skip the dialect step, so the real path (and therefore the
  // progress indicator) is shorter than the canonical nine.
  const steps = useMemo<readonly OnboardingStep[]>(() => {
    const skip = new Set<OnboardingStep>();
    if (draft.language !== null && !hasDialectChoice(draft.language)) skip.add('dialect');
    if (!anxiety.show) skip.add('anxiety');
    return ONBOARDING_STEPS.filter((candidate) => !skip.has(candidate));
  }, [draft.language, anxiety.show]);

  // Emitted as a side effect of rendering a step rather than from each transition, so
  // a step reached by a back button or a deep link counts the same as one reached by
  // tapping Continue.
  useEffect(() => {
    if (seenSteps.current.has(step)) return;
    seenSteps.current.add(step);
    const numbered: readonly OnboardingStep[] = steps.filter(
      (candidate) => candidate !== 'value' && candidate !== 'anxiety',
    );
    const index = numbered.indexOf(step);
    if (index >= 0) track('onboarding_step_view', { step: index + 1 });
  }, [step, steps, track]);

  const advance = useCallback(() => {
    const index = steps.indexOf(step);
    const next = steps[index + 1];
    if (next === undefined) props.onFinish();
    else onStep(next);
  }, [steps, step, onStep, props]);

  return (
    <main id="main" className="vl-page vl-onboarding">
      {!UNCOUNTED_STEPS.has(step) && (
        <ProgressDots
          total={steps.filter((candidate) => !UNCOUNTED_STEPS.has(candidate)).length}
          index={Math.max(
            0,
            steps.filter((candidate) => !UNCOUNTED_STEPS.has(candidate)).indexOf(step),
          )}
        />
      )}

      {step === 'value' && <ValueCarousel track={track} onDone={() => onStep('language')} />}

      {step === 'language' && (
        <LanguageStep
          track={track}
          onSelect={(language) => {
            track('onboarding_language_select', { lang: language });
            const dialects = DIALECTS_BY_LANGUAGE[language];
            onChange({
              ...draft,
              language,
              // Single-variant languages are resolved immediately, so the learner is
              // never shown a one-option choice.
              dialect: dialects.length === 1 ? (dialects[0] as Dialect) : null,
            });
            onStep(hasDialectChoice(language) ? 'dialect' : 'speak');
          }}
        />
      )}

      {step === 'dialect' && draft.language !== null && (
        <DialectStep
          language={draft.language}
          track={track}
          onPreview={props.onPreviewDialect ?? (() => undefined)}
          onSelect={(dialect) => {
            track('onboarding_dialect_select', { dialect });
            onChange({ ...draft, dialect });
            onStep('speak');
          }}
        />
      )}

      {step === 'speak' && draft.language !== null && (
        <SpeakStep
          language={draft.language}
          track={track}
          onPlayback={props.onPlayback}
          name={draft.name}
          onName={(name) => onChange({ ...draft, name })}
          score={draft.firstScore}
          micDenied={draft.micDenied}
          onRecord={async (heldMs) => {
            const retry = draft.firstScore !== null;
            if (retry) track('onboarding_retry', {});
            // `snr` is reported as a present null rather than a guess: the web recorder
            // does not measure signal-to-noise, and inventing a number here would put
            // a fabricated value in the same column as the real ones the mobile
            // recorders will send.
            track('onboarding_utterance_recorded', { ms: heldMs, snr: null });

            if (!firstUtteranceReported.current) {
              firstUtteranceReported.current = true;
              track('time_to_first_utterance_ms', { ms: Date.now() - startedAt.current });
            }

            const score = await props.onRecordFirstUtterance();
            track('onboarding_first_score', {
              accuracy: score.overall,
              band: score.band,
              // The confidence-gating wedge, measured: how often we decline to score is
              // the number that tells us whether the promise is real.
              confidence: score.withheld ? 'withheld' : 'scored',
            });
            if (score.band !== null) track('onboarding_score_reveal', { band: score.band });
            onChange({
              ...draft,
              firstScore: score,
              // Only the first take's length is a signal: a later one is informed by
              // having heard the prompt scored once already.
              firstUtteranceMs: draft.firstUtteranceMs ?? heldMs,
              speakRetries: draft.firstScore === null ? draft.speakRetries : draft.speakRetries + 1,
            });
          }}
          onRequestMic={async () => {
            const granted = await props.onRequestMic();
            track('onboarding_mic_permission', { granted });
            if (!granted) onChange({ ...draft, micDenied: true });
            return granted;
          }}
          onContinue={advance}
        />
      )}

      {step === 'placement' && (
        <PlacementStep
          words={props.placementWords}
          selected={draft.knownWordIds}
          micDenied={draft.micDenied}
          onToggle={(id) =>
            onChange({
              ...draft,
              knownWordIds: draft.knownWordIds.includes(id)
                ? draft.knownWordIds.filter((w) => w !== id)
                : [...draft.knownWordIds, id],
            })
          }
          onContinue={() => {
            track('onboarding_placement_complete', {
              cefr: props.estimatedLevel,
              // Both are reported as present nulls: the estimate comes back from the
              // API, and until the client is told the confidence and the vocabulary
              // size it will not guess at them.
              confidence: null,
              vocab_estimate: draft.knownWordIds.length,
              duration_ms: Date.now() - startedAt.current,
            });
            advance();
          }}
        />
      )}

      {step === 'plan_building' && (
        <PlanBuildingStep
          language={draft.language}
          dialect={draft.dialect}
          onFetchCatalogue={props.onFetchCatalogue}
          goals={draft.goals}
          minutes={draft.dailyGoalMinutes}
          level={props.estimatedLevel}
          // Their own words, to be quoted back. Verbi does this with "Ok, you already
          // know malo, corazón, grande" and it is the line that makes the pause feel
          // like work rather than a spinner
          // (docs/research/12-competitor-recordings-2026-10-19.md §2). Ours differs in
          // that these are words the learner actually tapped, not a sample.
          knownWords={props.placementWords
            .filter((word) => draft.knownWordIds.includes(word.id))
            .map((word) => word.display)}
          probeSkipped={draft.knownWordIds.length === 0}
          onDone={advance}
          track={track}
        />
      )}

      {/* The conversation when it can run, the tiles when it cannot. Both ask the
          same questions; the conversation also demonstrates the product, which is why
          the market leaders use it (docs/research/05-onboarding-patterns.md §3). */}
      {step === 'motivation' && !draft.micDenied && (
        <ConversationalQuiz
          draft={draft}
          onChange={onChange}
          persona={PERSONA_NAMES[draft.personaId] ?? 'your tutor'}
          track={track}
          onDone={() => {
            track('onboarding_motivation_select', {
              goals: draft.goals,
              exam: draft.goals.includes('exam') ? 'unspecified' : null,
            });
            onStep('tutor');
          }}
        />
      )}

      {step === 'motivation' && draft.micDenied && (
        <MotivationStep
          selected={draft.goals}
          track={track}
          onToggle={(id) =>
            onChange({
              ...draft,
              goals: draft.goals.includes(id)
                ? draft.goals.filter((g) => g !== id)
                : // Max two, so the signal stays meaningful.
                  [...draft.goals, id].slice(-2),
            })
          }
          onContinue={() => {
            track('onboarding_motivation_select', {
              goals: draft.goals,
              exam: draft.goals.includes('exam') ? 'unspecified' : null,
            });
            advance();
          }}
        />
      )}

      {/* Reached only on the fallback path: the conversation asks for minutes as its
          second turn and goes straight to the tutor picker. */}
      {step === 'anxiety' && anxiety.signal !== null && (
        <AnxietyStep
          signal={anxiety.signal}
          track={track}
          onContinue={(choseTextMode) => {
            track('onboarding_anxiety_ack_continue', { chose_text_mode: choseTextMode });
            // Choosing text here is a real choice, not a detour: it sets the mode the
            // first lesson opens in, and nothing else about onboarding changes.
            onChange({
              ...draft,
              anxietyAckSeen: true,
              preferredMode: choseTextMode ? 'text' : 'voice',
            });
            advance();
          }}
        />
      )}

      {step === 'commitment' && (
        <CommitmentStep
          minutes={draft.dailyGoalMinutes}
          reminder={draft.reminderLocalTime}
          onMinutes={(dailyGoalMinutes) => onChange({ ...draft, dailyGoalMinutes })}
          onReminder={(reminderLocalTime) => onChange({ ...draft, reminderLocalTime })}
          onContinue={() => {
            track('onboarding_goal_set', {
              minutes: draft.dailyGoalMinutes,
              reminder_local_time: draft.reminderLocalTime,
            });
            advance();
          }}
        />
      )}

      {step === 'tutor' && (
        <TutorStep
          personaId={draft.personaId}
          strictness={draft.strictness}
          correctionStyle={draft.correctionStyle}
          onPersona={(personaId) => {
            onChange({ ...draft, personaId });
          }}
          onPreview={(personaId) => {
            // Fires on *listening* now, not on selecting — which is what the event was
            // always named for and never measured.
            track('onboarding_tutor_preview', { persona: personaId });
            props.onPreviewPersona?.(personaId);
          }}
          onStrictness={(strictness) => onChange({ ...draft, strictness })}
          onCorrectionStyle={(correctionStyle) => onChange({ ...draft, correctionStyle })}
          onContinue={() => {
            track('onboarding_tutor_select', {
              persona: draft.personaId,
              strictness: draft.strictness,
              correction_style: draft.correctionStyle,
            });
            advance();
          }}
        />
      )}

      {step === 'notifications' && (
        <NotificationsStep
          reminder={draft.reminderLocalTime}
          track={track}
          onAllow={async () => {
            const allowed = await props.onRequestNotifications();
            track('onboarding_notif_permission', { granted: allowed });
            onChange({ ...draft, notificationsAllowed: allowed });
            advance();
          }}
          onSkip={() => {
            track('onboarding_notif_permission', { granted: false });
            onChange({ ...draft, notificationsAllowed: false });
            advance();
          }}
        />
      )}

      {step === 'plan' && (
        <PlanStep
          level={props.estimatedLevel}
          minutes={draft.dailyGoalMinutes}
          // Led by whichever promise answers what they said went wrong last time. The
          // tutor already replied in one line during the quiz; this is the same
          // commitment with the mechanism spelled out, on the screen immediately
          // before the first lesson.
          promises={promisesFor(draft.priorAppFailure)}
          basis={planBasis({
            level: props.estimatedLevel,
            // Only for a language that offered a choice: listing a dialect the learner
            // never picked would be an invented row.
            dialectLabel:
              draft.dialect !== null && draft.language !== null && hasDialectChoice(draft.language)
                ? draft.dialect
                : null,
            // Gated on the step having been seen, because `emptyDraft` seeds both of
            // these. A seeded default is not an answer, and "You committed to 10
            // minutes" is false for a learner who never reached the commitment step —
            // the same reason voice mode is not listed as a choice. A render test for
            // the skipped-everything case caught both of these claiming credit.
            dailyGoalMinutes: seenSteps.current.has('commitment') ? draft.dailyGoalMinutes : 0,
            goals: draft.goals,
            priorAppFailure: draft.priorAppFailure,
            knownWordCount: draft.knownWordIds.length,
            preferredMode: draft.preferredMode === 'text' ? 'text' : 'voice',
            personaName: seenSteps.current.has('tutor')
              ? (PERSONA_NAMES[draft.personaId] ?? null)
              : null,
          })}
          onStart={() => {
            track('onboarding_complete', {
              duration_ms: Date.now() - startedAt.current,
              steps_shown: seenSteps.current.size,
              cefr: props.estimatedLevel,
            });
            track('onboarding_first_action', { action: 'start_lesson' });
            props.onFinish();
          }}
        />
      )}

      {/* Asserted by tests: no paywall exists anywhere in this flow. */}
      <p className="vl-onboarding__promise">
        No card needed. You get 10 minutes of speaking a day, free, forever.
      </p>
    </main>
  );
}

function ProgressDots({ total, index }: { total: number; index: number }): JSX.Element {
  return (
    <ol className="vl-dots" aria-label={`Step ${index + 1} of ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length step indicator; it never reorders and holds no state.
          key={i}
          className="vl-dots__dot"
          data-state={i < index ? 'done' : i === index ? 'current' : 'todo'}
        />
      ))}
    </ol>
  );
}

/**
 * Languages we do not teach yet, listed rather than hidden.
 *
 * Taken from Speak, which shows Simplified Chinese and "Other" as JOIN WAITLIST. It
 * captures demand we would otherwise never see, and it is honest about the gap instead
 * of implying the grid is the whole world.
 */
const WAITLIST_LANGUAGES: readonly string[] = [
  'Arabic',
  'Hindi',
  'Russian',
  'Turkish',
  'Vietnamese',
  'Polish',
];

/**
 * The sentence the learner is asked to say, for the scorer.
 *
 * Exported because the container has to send the same text the screen displayed:
 * scoring against a different sentence would mark a correct utterance wrong.
 */
export function firstSentence(language: Language): string {
  return FIRST_SENTENCE[language].text;
}

function LanguageStep({
  onSelect,
  track,
}: {
  onSelect: (language: Language) => void;
  track: Track;
}): JSX.Element {
  const [requested, setRequested] = useState<string | null>(null);
  const [other, setOther] = useState('');

  const request = (name: string) => {
    if (name.trim().length === 0) return;
    // The text, because the point is to find out what people are asking for. A count
    // of taps on "Other" would tell us demand exists and nothing about what for.
    track('onboarding_language_requested', { text: name.trim() });
    setRequested(name.trim());
  };

  return (
    <section className="vl-step">
      <h1 className="vl-step__title">What do you want to learn?</h1>
      <ul className="vl-grid vl-grid--2">
        {LANGUAGES.map((language) => (
          <li key={language.id}>
            <button type="button" className="vl-tile" onClick={() => onSelect(language.id)}>
              <span className="vl-tile__title">{language.endonym}</span>
              <span className="vl-tile__detail">{language.name}</span>
            </button>
          </li>
        ))}
      </ul>

      <section className="vl-step__waitlist" aria-label="Languages we don't teach yet">
        <h2 className="vl-step__subhead">Not here yet</h2>
        {requested === null ? (
          <>
            <ul className="vl-chip-list">
              {WAITLIST_LANGUAGES.map((name) => (
                <li key={name}>
                  <button type="button" className="vl-chip-filter" onClick={() => request(name)}>
                    {name}
                  </button>
                </li>
              ))}
            </ul>
            <form
              className="vl-field"
              onSubmit={(event) => {
                event.preventDefault();
                request(other);
              }}
            >
              <label className="vl-field__label" htmlFor="vl-other-language">
                Something else?
              </label>
              <input
                id="vl-other-language"
                className="vl-field__input"
                value={other}
                onChange={(event) => setOther(event.target.value)}
                placeholder="Swahili"
                autoComplete="off"
              />
            </form>
          </>
        ) : (
          // No promise about when. A waitlist that implies a date is a date we would
          // then have to break.
          <p className="vl-step__fineprint">
            Noted — {requested}. We will not pretend to know when, but this is how we decide what to
            build next.
          </p>
        )}
      </section>
    </section>
  );
}

function DialectStep({
  language,
  onSelect,
  onPreview,
  track,
}: {
  language: Language;
  onSelect: (dialect: Dialect) => void;
  /** Plays the same sentence in this dialect, so the two are actually comparable. */
  onPreview: (dialect: Dialect) => void;
  track: Track;
}): JSX.Element {
  const dialects = DIALECTS_BY_LANGUAGE[language];
  return (
    <section className="vl-step">
      <h1 className="vl-step__title">Which flavour?</h1>
      {/* Wedge W3: no competitor in the set makes dialect a first-class choice. */}
      <p className="vl-step__body">
        Dialect changes the vocabulary you learn, the voice you hear, and what counts as correct
        when we score you.
      </p>
      <ul className="vl-grid vl-grid--2">
        {dialects.map((dialect) => {
          const copy = DIALECT_COPY[dialect];
          return (
            <li key={dialect}>
              <button
                type="button"
                className="vl-tile vl-tile--lg"
                onClick={() => onSelect(dialect)}
              >
                <span className="vl-tile__title">{copy?.name ?? dialect}</span>
                <span className="vl-tile__detail">{copy?.detail ?? ''}</span>
              </button>
              {/* The same sentence in both, which is the only way a learner who has
                  never heard the difference can actually choose. Separate from the
                  card so hearing it is not the same gesture as picking it. */}
              <button
                type="button"
                className="vl-button vl-button--ghost"
                onClick={() => {
                  track('onboarding_dialect_preview', { dialect });
                  onPreview(dialect);
                }}
              >
                ▶ Hear it
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SpeakStep({
  language,
  name,
  onName,
  score,
  micDenied,
  onRecord,
  onRequestMic,
  onContinue,
  track,
  onPlayback,
}: {
  language: Language;
  name: string;
  onName: (name: string) => void;
  score: FirstScore | null;
  micDenied: boolean;
  /** `heldMs` is how long they held the button — the A6b short-utterance signal. */
  onRecord: (heldMs: number) => Promise<void>;
  onRequestMic: () => Promise<boolean>;
  onContinue: () => void;
  track: Track;
  /** Plays one side of the A/B comparison. */
  onPlayback?: ((which: 'you' | 'coach' | 'coach_slow') => void) | undefined;
}): JSX.Element {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const startedAt = useRef<number | null>(null);

  // Emitted when the prompt is actually on screen, which is the denominator for the
  // permission grant rate: a grant rate measured against app opens is meaningless.
  useEffect(() => {
    if (micDenied || score !== null) return;
    track('onboarding_mic_prompt_shown', {});
  }, [micDenied, score, track]);
  const sentence = FIRST_SENTENCE[language];
  const fullText = name.length > 0 ? `${sentence.text} ${name}` : `${sentence.text} ___`;

  // Mic denied is not a dead end: onboarding continues through the word grid.
  if (micDenied) {
    return (
      <section className="vl-step">
        <h1 className="vl-step__title">No problem — we&apos;ll work it out from words</h1>
        <Banner
          tone="permission"
          action={{ label: 'Enable microphone', onClick: () => void onRequestMic() }}
        >
          Microphone access is off. Everything except live speaking still works.
        </Banner>
        <Button onClick={onContinue}>Continue</Button>
      </section>
    );
  }

  if (score !== null) {
    return (
      <section className="vl-step vl-step--reveal">
        <h1 className="vl-step__title">Here&apos;s where you are</h1>
        <ScoreRing
          score={score.overall}
          status={score.withheld ? 'withheld' : 'scored'}
          size="lg"
          label="Your first score"
        />
        {score.withheld ? (
          <WithheldNotice
            reason="too_quiet"
            message={score.withheldMessage}
            onRetry={() => {
              void onRecord(0);
            }}
          />
        ) : (
          // Exactly one diagnostic sentence — specific, and fixable.
          <p className="vl-step__note">{score.note}</p>
        )}
        {/* The A/B row. Hearing yourself next to the model is the comparison that
            makes a score mean something, and it is the moment the learner decides
            whether the scoring is credible. */}
        <fieldset className="vl-playback">
          <legend>Compare your attempt</legend>
          {(
            [
              ['you', '▶ You'],
              ['coach', '▶ Coach'],
              ['coach_slow', '🐌 Coach, slow'],
            ] as const
          ).map(([which, label]) => (
            <button
              key={which}
              type="button"
              className="vl-button vl-button--ghost"
              onClick={() => {
                track('onboarding_ab_playback', { which });
                onPlayback?.(which);
              }}
            >
              {label}
            </button>
          ))}
        </fieldset>

        <div className="vl-step__actions">
          <Button variant="secondary" onClick={() => void onRecord(0)}>
            Try again
          </Button>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="vl-step">
      <h1 className="vl-step__title">Say this out loud</h1>
      <TargetSentence
        text={fullText}
        respelling={sentence.respelling}
        ipa={sentence.ipa}
        lang={language}
      />

      <label className="vl-field">
        <span className="vl-field__label">Your name</span>
        <input
          className="vl-field__input"
          value={name}
          onChange={(event) => onName(event.target.value)}
          placeholder="Ana"
          autoComplete="given-name"
        />
      </label>

      {/* Above the button, not below it.
          The tap is what triggers the browser's permission dialog, so an explanation
          printed underneath is read after the decision has been asked for. Verbi gives
          this its own screen before the system prompt
          (docs/research/12-competitor-recordings-2026-10-19.md §2); the screen is
          unnecessary when the prompt is this self-evident, but the order is not. */}
      <p className="vl-step__fineprint">
        We only use your microphone for this. Nothing is shared, and you can delete it any time.
      </p>

      <MicButton
        state={processing ? 'processing' : recording ? 'listening' : 'idle'}
        size="lg"
        onStart={() => {
          setRecording(true);
          startedAt.current = Date.now();
        }}
        onStop={() => {
          setRecording(false);
          setProcessing(true);
          const held = startedAt.current === null ? 0 : Date.now() - startedAt.current;
          startedAt.current = null;
          void onRecord(held).finally(() => setProcessing(false));
        }}
        onRequestPermission={() => void onRequestMic()}
      />
    </section>
  );
}

/**
 * A6b. One screen, shown only on a signal, that names the fear plainly.
 *
 * Loora runs the equivalent screen and ScreensDesign scores it the single highest unique
 * moment in its onboarding; no other app in the twenty studied addresses the fear at all
 * (docs/research/05-onboarding-patterns.md). It builds nothing new — both reassurances
 * are mechanisms we already have — which is the point: we can make this promise because
 * `withheld` is a real state and text mode is a real path.
 */
function AnxietyStep({
  signal,
  onContinue,
  track,
}: {
  readonly signal: AnxietySignal;
  readonly onContinue: (choseTextMode: boolean) => void;
  readonly track: Track;
}): JSX.Element {
  const copy = anxietyAckCopy(signal);

  // The signal is the whole point of the event: which of the three cues brought the
  // learner here is what tells us whether the detection is worth keeping.
  useEffect(() => {
    track('onboarding_anxiety_ack_view', { signal });
  }, [signal, track]);

  return (
    <section className="vl-step vl-step--anxiety" aria-labelledby="vl-anxiety-title">
      <h1 className="vl-step__title" id="vl-anxiety-title">
        {copy.title}
      </h1>
      <p className="vl-step__body">{copy.body}</p>

      <ul className="vl-anxiety__reassurances">
        {copy.reassurances.map((reassurance) => (
          <li key={reassurance}>{reassurance}</li>
        ))}
      </ul>

      <div className="vl-step__actions">
        <Button onClick={() => onContinue(false)}>{copy.primaryCta}</Button>
        {/* Equal weight is deliberate. A text-mode link styled as a consolation prize
            tells the learner it is one. */}
        <Button variant="secondary" onClick={() => onContinue(true)}>
          {copy.secondaryCta}
        </Button>
      </div>
    </section>
  );
}

/**
 * A6c · The learner's own answers, being turned into a plan.
 *
 * Four questions answered and nothing visibly done with them is what makes an
 * onboarding quiz feel like a tax. Learna ($6M/month) scores highest in the category
 * on exactly this screen; every line here names a real input the learner gave, and a
 * line is left out rather than invented when they skipped the question.
 */
function PlanBuildingStep({
  language,
  dialect,
  goals,
  minutes,
  level,
  knownWords,
  probeSkipped,
  onFetchCatalogue,
  onDone,
  track,
}: {
  readonly language: Language | null;
  readonly dialect: Dialect | null;
  readonly onFetchCatalogue?:
    | ((dialect: Dialect) => Promise<{ lessons: number; scenarios: number; items: number } | null>)
    | undefined;
  readonly goals: readonly string[];
  readonly minutes: number;
  readonly level: string | null;
  /** Words the learner tapped as known in the probe, in corpus order. */
  readonly knownWords: readonly string[];
  readonly probeSkipped: boolean;
  readonly onDone: () => void;
  readonly track: Track;
}): JSX.Element {
  const startedAt = useRef(Date.now());
  const [catalogue, setCatalogue] = useState<{
    lessons: number;
    scenarios: number;
    items: number;
  } | null>(null);

  useEffect(() => {
    if (dialect === null || onFetchCatalogue === undefined) return;
    let live = true;
    void onFetchCatalogue(dialect).then((result) => {
      if (live) setCatalogue(result);
    });
    return () => {
      live = false;
    };
  }, [dialect, onFetchCatalogue]);

  const lines = useMemo(() => {
    const built: string[] = [];
    if (language !== null) {
      built.push(dialect === null ? `Course: ${language}` : `Course: ${language} · ${dialect}`);
    }
    if (goals.length > 0) {
      built.push(`Built around ${goals.join(' and ')}`);
    }
    // Their own words, quoted back. Three at most: the point is recognition, and a
    // list of fifteen is a list. Nothing is said when they tapped none, because an
    // invented example is the lie this screen exists to avoid.
    if (knownWords.length > 0) {
      built.push(`You already know ${knownWords.slice(0, 3).join(', ')}`);
    }
    built.push(`${minutes} minutes a day`);
    // Left out rather than guessed at: a learner who skipped the probe did not give
    // us a level, and showing one anyway is the lie this screen exists to avoid.
    if (!probeSkipped && level !== null) built.push(`Starting at ${level}`);
    // Real counts from the published corpus, or nothing. A number invented to make the
    // pause feel substantial is the same lie as an invented level, one line up.
    if (catalogue !== null) {
      if (catalogue.scenarios > 0) {
        built.push(`${catalogue.scenarios.toLocaleString()} scenarios to practise in`);
      }
      if (catalogue.items > 0) {
        built.push(`${catalogue.items.toLocaleString()} words and phrases`);
      }
    }
    built.push('First lesson ready');
    return built;
  }, [language, dialect, goals, minutes, level, knownWords, probeSkipped, catalogue]);

  // Ticks in one line at a time, then moves on by itself. The learner gave the answers
  // already; making them tap again to acknowledge their own input is a step too many.
  const [shown, setShown] = useState(1);
  useEffect(() => {
    if (shown >= lines.length) {
      track('onboarding_plan_built', {
        items: lines.length,
        ms: Date.now() - startedAt.current,
      });
      const done = setTimeout(onDone, 700);
      return () => clearTimeout(done);
    }
    const next = setTimeout(() => setShown((n) => n + 1), 450);
    return () => clearTimeout(next);
  }, [shown, lines.length, onDone, track]);

  return (
    <section className="vl-step vl-plan-building" aria-live="polite">
      <h1 className="vl-step__title">Building your plan</h1>
      <ol className="vl-plan-building__lines">
        {lines.slice(0, shown).map((line) => (
          <li key={line}>
            <span aria-hidden="true">✓</span> {line}
          </li>
        ))}
      </ol>
      {probeSkipped && (
        <p className="vl-step__fineprint">
          You skipped the words, so we will set your level from how you actually speak.
        </p>
      )}
    </section>
  );
}

function PlacementStep({
  words,
  selected,
  micDenied,
  onToggle,
  onContinue,
}: {
  words: readonly { id: string; display: string }[];
  selected: readonly string[];
  micDenied: boolean;
  onToggle: (id: string) => void;
  onContinue: () => void;
}): JSX.Element {
  return (
    <section className="vl-step">
      <h1 className="vl-step__title">Tap the words you already know</h1>
      <p className="vl-step__body">
        {micDenied
          ? 'This is how we work out your level without the microphone.'
          : 'Be honest — this sets your starting level.'}
      </p>
      <ul className="vl-chips">
        {words.map((word) => (
          <li key={word.id}>
            <button
              type="button"
              className="vl-chip"
              aria-pressed={selected.includes(word.id)}
              onClick={() => onToggle(word.id)}
            >
              {word.display}
            </button>
          </li>
        ))}
      </ul>
      <Button onClick={onContinue}>
        Continue{selected.length > 0 ? ` (${selected.length} selected)` : ''}
      </Button>
    </section>
  );
}

/**
 * The personalisation questions, asked by the tutor the learner just spoke to.
 *
 * Four of the highest-revenue apps in the category run their quiz as a conversation
 * rather than a form — Speak ($3.25M/mo), Learna ($6M/mo), BetterSpeak and ELSA — and
 * it carries the highest uniqueness score ScreensDesign assigned to anything in the
 * set (docs/research/05-onboarding-patterns.md §3). Their shared reasoning is that the
 * quiz stops being a tax on the learner and becomes a demonstration of the product.
 *
 * Answers are chips rather than free text. A learner three screens into an app they
 * have not paid for will not type a sentence, and a chip is a legitimate turn: the
 * tutor acknowledges it in the thread, so the conversation holds either way.
 *
 * `MotivationStep` and `CommitmentStep` remain as the fallback, used whenever the
 * conversation cannot run — microphone denied, voice exhausted, model unavailable. It
 * is not a lesser path; it is the same questions, faster.
 */
/** Learner-facing names, so the thread says who is talking. */
const PERSONA_NAMES: Readonly<Record<string, string>> = {
  mika: 'Mika',
  dani: 'Dani',
  sol: 'Sol',
  rex: 'Rex',
  vex: 'Vex',
  nakamura: 'Nakamura',
};

function ConversationalQuiz({
  draft,
  onChange,
  onDone,
  persona,
  track,
}: {
  draft: OnboardingDraft;
  onChange: (draft: OnboardingDraft) => void;
  onDone: () => void;
  persona: string;
  track: Track;
}): JSX.Element {
  const [turn, setTurn] = useState(0);
  const threadEnd = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  if (!started.current) {
    started.current = true;
    track('onboarding_conversational_quiz_start', { persona });
  }

  const questions = [
    {
      id: 'goals',
      ask: 'So — what are you learning this for?',
      options: GOALS.map((goal) => ({ id: goal.id, label: goal.label })),
      reply: (id: string) =>
        id === 'travel'
          ? "Travel it is. We'll start with the things you actually say on a trip."
          : id === 'work'
            ? "Work. Then we'll spend the time on being understood, not on being perfect."
            : 'Good. That changes what we practise first.',
    },
    {
      // Asked as a turn rather than as its own step, so it personalises the pitch
      // without making onboarding a question longer. Verbi gives it two whole screens
      // (docs/research/12-competitor-recordings-2026-10-19.md §2); the tutor can ask
      // it in the conversation that is already happening.
      id: 'prior_app',
      ask: 'Before we start — have you tried another app for this?',
      options: [
        { id: 'wanted_money_first', label: 'Yes, it wanted money first' },
        { id: 'marked_me_wrong', label: 'Yes, it marked me wrong' },
        { id: 'never_spoke', label: 'Yes, it never got me speaking' },
        { id: 'lost_interest', label: 'Yes, I lost interest' },
        { id: 'none', label: "No, you're my first" },
      ],
      // The reply is the promise, in one line, with the mechanism in it. The full card
      // is shown on the plan screen; this is the tutor answering the complaint.
      reply: (id: string) =>
        id === 'none'
          ? "Then I'll keep it simple: ten minutes of talking a day, free, for as long as you want it."
          : promiseFor(id === 'none' ? null : (id as PriorAppFailure)).title + '.',
    },
    {
      id: 'minutes',
      ask: 'And how much time can you give it on a normal day?',
      options: [
        ...DAILY_GOAL_CHOICES.map((minutes) => ({
          id: String(minutes),
          label: `${minutes} min`,
        })),
      ],
      // The projection is the honest part: stated as an estimate, from our own model.
      // Read from `packages/core` rather than written here, because the settings screen
      // shows the same sentence when a learner changes the goal, and two copies of a
      // claim about outcomes is how they stop agreeing.
      reply: (id: string) => commitmentProjection(Number(id)),
    },
  ] as const;

  const current = questions[turn];
  const answered = questions.slice(0, turn);

  // Keeps the newest turn in view without stealing focus, which a scroll-into-view on
  // the control itself would do.
  useEffect(() => {
    // Guarded: jsdom has no layout, so `scrollIntoView` is simply absent there. A
    // convenience that throws in a test environment is a convenience that breaks the
    // screen for anyone whose browser lacks it too.
    threadEnd.current?.scrollIntoView?.({ block: 'end' });
  }, []);

  /// Goals are chosen within a single turn rather than across two.
  ///
  /// The contract takes up to two, and "work" plus "moving abroad" is a real pair —
  /// but asking a follow-up for the second would add a turn to a flow whose whole
  /// advantage is brevity. Selecting stays in one exchange; the tutor replies once.
  function toggleGoal(id: string) {
    // `answered_by` records which affordance answered the turn. The conversational
    // version is the one the market leaders run, and the comparison against the tile
    // fallback is the only way to know whether it earns its extra complexity here.
    track('onboarding_quiz_turn', { index: 1, answered_by: 'tiles_in_thread' });
    onChange({
      ...draft,
      goals: draft.goals.includes(id)
        ? draft.goals.filter((goal) => goal !== id)
        : // Max two, so the signal stays meaningful.
          [...draft.goals, id].slice(-2),
    });
  }

  /**
   * What the learner answered, by question rather than by position.
   *
   * These were two inline ternaries keyed on the index, which silently mapped the
   * wrong answer under every question the moment a third one was added between them.
   */
  function answerId(id: string): string {
    if (id === 'goals') return draft.goals[0] ?? '';
    if (id === 'prior_app') return draft.priorAppFailure ?? 'none';
    return String(draft.dailyGoalMinutes);
  }

  function answerLabel(id: string): string {
    const question = questions.find((candidate) => candidate.id === id);
    if (id === 'goals') {
      return draft.goals
        .map((goal) => question?.options.find((option) => option.id === goal)?.label)
        .filter(Boolean)
        .join(' and ');
    }
    return question?.options.find((option) => option.id === answerId(id))?.label ?? '';
  }

  /**
   * Records what went wrong with the last app, and which promise answers it.
   *
   * The distribution is the point: if most learners say the last app wanted money
   * first, the free tier is the product rather than a concession.
   */
  function answerPriorApp(id: string) {
    track('onboarding_quiz_turn', { index: 1, answered_by: 'tiles_in_thread' });
    const failure = id === 'none' ? null : (id as PriorAppFailure);
    track('onboarding_prior_app', {
      failure: failure ?? 'none',
      promise: promiseFor(failure).id,
    });
    onChange({ ...draft, priorAppFailure: failure });
    setTurn(2);
  }

  function answerMinutes(id: string) {
    track('onboarding_quiz_turn', { index: 2, answered_by: 'tiles_in_thread' });
    onChange({ ...draft, dailyGoalMinutes: Number(id) });
    // Advances past the questions rather than leaving: the tutor's reply to the last
    // answer is the one that carries the projection, and jumping straight to the next
    // screen means the learner never sees it. A conversation that stops replying the
    // moment it has what it wanted is a form wearing a thread.
    setTurn(questions.length);
  }

  return (
    <section className="vl-step vl-quiz" aria-label="Setting up with your tutor">
      {/* A live region, so a screen-reader user hears each new turn rather than
          having to go looking for it. */}
      <ol className="vl-quiz__thread" aria-live="polite">
        {answered.map((question) => (
          <li key={question.id} className="vl-quiz__exchange">
            <p className="vl-quiz__tutor">{question.ask}</p>
            <p className="vl-quiz__learner">{answerLabel(question.id)}</p>
            <p className="vl-quiz__tutor">{question.reply(answerId(question.id))}</p>
          </li>
        ))}

        {current !== undefined && (
          <li className="vl-quiz__exchange">
            <p className="vl-quiz__tutor">{current.ask}</p>
          </li>
        )}
        <div ref={threadEnd} />
      </ol>

      {current !== undefined && (
        <ul className="vl-quiz__chips">
          {current.options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="vl-chip"
                aria-pressed={current.id === 'goals' ? draft.goals.includes(option.id) : undefined}
                onClick={() => {
                  if (current.id === 'prior_app') return answerPriorApp(option.id);
                  if (current.id === 'goals') return toggleGoal(option.id);
                  return answerMinutes(option.id);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {current?.id === 'goals' && (
        <Button onClick={() => setTurn(1)} disabled={draft.goals.length === 0}>
          Continue
        </Button>
      )}

      {current === undefined && <Button onClick={onDone}>Sounds right</Button>}

      <p className="vl-quiz__who">You're talking to {persona}.</p>
    </section>
  );
}

function MotivationStep({
  selected,
  track,
  onToggle,
  onContinue,
}: {
  selected: readonly string[];
  track: Track;
  onToggle: (id: string) => void;
  onContinue: () => void;
}): JSX.Element {
  const reported = useRef(false);
  if (!reported.current) {
    reported.current = true;
    // Reached only when the conversation cannot run, which is the thing worth counting:
    // how often the fallback is the experience a learner actually gets.
    track('onboarding_quiz_fallback', { reason: 'mic_denied' });
  }

  return (
    <section className="vl-step">
      <h1 className="vl-step__title">Why are you learning?</h1>
      <p className="vl-step__body">Pick up to two.</p>
      <ul className="vl-grid vl-grid--2">
        {GOALS.map((goal) => (
          <li key={goal.id}>
            <button
              type="button"
              className="vl-tile"
              aria-pressed={selected.includes(goal.id)}
              onClick={() => onToggle(goal.id)}
            >
              <span className="vl-tile__title">{goal.label}</span>
            </button>
          </li>
        ))}
      </ul>
      <Button onClick={onContinue} disabled={selected.length === 0}>
        Continue
      </Button>
    </section>
  );
}

function CommitmentStep({
  minutes,
  reminder,
  onMinutes,
  onReminder,
  onContinue,
}: {
  minutes: number;
  reminder: string;
  onMinutes: (minutes: number) => void;
  onReminder: (time: string) => void;
  onContinue: () => void;
}): JSX.Element {
  // Honest projections, labelled as estimates.
  const projections: Record<number, string> = {
    5: '~A2 → B1 in about 14 months',
    10: '~A2 → B1 in about 7 months',
    15: '~A2 → B1 in about 5 months',
    30: '~A2 → B1 in about 3 months',
  };

  return (
    <section className="vl-step">
      <h1 className="vl-step__title">How much time a day?</h1>
      <ul className="vl-grid vl-grid--2">
        {[5, 10, 15, 30].map((value) => (
          <li key={value}>
            <button
              type="button"
              className="vl-tile"
              aria-pressed={minutes === value}
              onClick={() => onMinutes(value)}
            >
              <span className="vl-tile__title">{value} min</span>
              <span className="vl-tile__detail">{projections[value]} (estimate)</span>
            </button>
          </li>
        ))}
      </ul>

      <label className="vl-field">
        <span className="vl-field__label">Remind me at</span>
        <input
          className="vl-field__input"
          type="time"
          value={reminder}
          onChange={(event) => onReminder(event.target.value)}
        />
      </label>

      <Button onClick={onContinue}>Continue</Button>
    </section>
  );
}

function TutorStep({
  personaId,
  strictness,
  correctionStyle,
  onPersona,
  onPreview,
  onStrictness,
  onCorrectionStyle,
  onContinue,
}: {
  personaId: string;
  strictness: number;
  correctionStyle: 'inline' | 'after_turn' | 'end_of_session';
  onPersona: (id: string) => void;
  onPreview: (id: string) => void;
  onStrictness: (value: number) => void;
  onCorrectionStyle: (value: 'inline' | 'after_turn' | 'end_of_session') => void;
  onContinue: () => void;
}): JSX.Element {
  return (
    <section className="vl-step">
      <h1 className="vl-step__title">Pick your tutor</h1>
      <ul className="vl-grid vl-grid--2">
        {PERSONAS.map((persona) => (
          <li key={persona.id}>
            <button
              type="button"
              className="vl-tile"
              aria-pressed={personaId === persona.id}
              onClick={() => onPersona(persona.id)}
            >
              <span className="vl-tile__title">{persona.name}</span>
              <span className="vl-tile__detail">{persona.character}</span>
            </button>
            {/* Hearing the manner is the choice. A paragraph describing a tutor as
                "blunt" is not the same information as hearing one, and research across
                twelve competitors scored sampling the AI partner as the most original
                mechanic in the category. Separate from the tile, like the dialect
                preview, so listening is not the same gesture as picking. */}
            <button
              type="button"
              className="vl-button vl-button--ghost"
              onClick={() => onPreview(persona.id)}
            >
              ▶ Hear {persona.name}
            </button>
          </li>
        ))}
      </ul>

      <label className="vl-field">
        <span className="vl-field__label">
          How strict? {strictness < 0.4 ? 'Gentle' : strictness < 0.7 ? 'Balanced' : 'Demanding'}
        </span>
        <input
          className="vl-field__range"
          type="range"
          min={0}
          max={1}
          step={0.1}
          value={strictness}
          onChange={(event) => onStrictness(Number(event.target.value))}
        />
      </label>

      <fieldset className="vl-fieldset">
        <legend className="vl-field__label">When should corrections come?</legend>
        {(
          [
            ['inline', 'In the moment'],
            ['after_turn', 'After each turn'],
            ['end_of_session', 'At the end'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="vl-radio">
            <input
              type="radio"
              name="correction-style"
              value={value}
              checked={correctionStyle === value}
              onChange={() => onCorrectionStyle(value)}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <Button onClick={onContinue}>Continue</Button>
    </section>
  );
}

function NotificationsStep({
  reminder,
  track,
  onAllow,
  onSkip,
}: {
  reminder: string;
  track: Track;
  onAllow: () => Promise<void> | void;
  onSkip: () => void;
}): JSX.Element {
  useEffect(() => {
    track('onboarding_notif_warmup_view', {});
  }, [track]);

  return (
    <section className="vl-step">
      <h1 className="vl-step__title">One reminder a day</h1>
      {/* The exact contract, stated before the system prompt. */}
      <p className="vl-step__body">
        One reminder at {reminder}. A nudge if your streak is at risk. Nothing else, ever — and
        never more than two a day.
      </p>
      <div className="vl-step__actions">
        <Button onClick={() => void onAllow()}>Allow notifications</Button>
        <Button variant="ghost" onClick={onSkip}>
          Not now
        </Button>
      </div>
    </section>
  );
}

function PlanStep({
  level,
  minutes,
  promises,
  basis,
  onStart,
}: {
  level: string | null;
  minutes: number;
  /** Led by the one that answers what went wrong with their last app. */
  readonly promises: readonly PromiseCard[];
  /** The derivation: each answer they gave, and what it changed. May be empty. */
  readonly basis: readonly PlanBasisRow[];
  onStart: () => void;
}): JSX.Element {
  return (
    <section className="vl-step">
      <h1 className="vl-step__title">You&apos;re at {level ?? 'A1.1'}</h1>
      <p className="vl-step__body">
        Your plan is {minutes} minutes a day. We&apos;ll adjust it as we learn more about you — and
        we&apos;ll always show you how we worked it out.
      </p>

      {/* The derivation, taken from Speak ($3.25M/mo) and Lingo AI, both of which replay
          the learner's answers back before asking for anything
          (docs/research/16-category-leaders-2026-10-20.md §4). Nine steps that vanish
          without trace read as a form; nine steps with consequences read as a setup.
          Rows exist only for answers actually given — `planBasis` invents none, so a
          learner who skipped everything sees no section rather than an empty heading. */}
      {basis.length > 0 && (
        <div className="vl-plan-basis">
          <h2 className="vl-plan-basis__title">Built from what you told us</h2>
          <ul>
            {basis.map((row) => (
              <li key={row.id}>
                <strong>{row.youSaid}</strong> — so we {row.soWe}.
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Three commitments, each with a mechanism rather than a slogan, led by the one
          that answers the complaint they brought with them. This is the screen before
          the first lesson, which is where both competitors put their paywall. */}
      <ul className="vl-promises">
        {promises.map((promise, index) => (
          <li key={promise.id} className="vl-promises__card" data-lead={index === 0}>
            <h2>{promise.title}</h2>
            <p>{promise.body}</p>
          </li>
        ))}
      </ul>

      <Button size="lg" onClick={onStart}>
        Start lesson 1
      </Button>
    </section>
  );
}
